import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, subtotal, applyDiscount, isDiscountApplied } = useCart();
  const [coupon, setCoupon] = useState('');
  const [couponError, setCouponError] = useState('');

  const shipping = subtotal >= 75 ? 0 : 5.99;
  const finalDiscount = isDiscountApplied ? subtotal * 0.15 : 0;
  const total = subtotal + shipping - finalDiscount;

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    if (applyDiscount(coupon)) {
      setCouponError('');
    } else {
      setCouponError('Invalid discount code.');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white px-4">
        <SafeIcon name="ShoppingBag" className="w-16 h-16 text-sand mb-6" />
        <h1 className="font-serif text-3xl mb-4">Your bag is empty</h1>
        <Button to="/shop">Shop Ube Essentials</Button>
      </div>
    );
  }

  return (
    <div className="bg-sand-light min-h-screen pt-12 pb-24 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl mb-12">Shopping Bag</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {cart.map(item => (
              <div key={item.cartKey} className="bg-white rounded-3xl p-6 flex gap-6 shadow-sm border border-sand">
                <div className="w-24 h-32 rounded-xl overflow-hidden bg-sand-light flex-shrink-0">
                  <img src={item.image} alt={item.altText || item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-grow flex flex-col">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-serif text-xl text-gray-900">{item.name}</h3>
                      <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">
                        {item.variant ? item.variant.name : item.size} {item.isSubscription && " • Monthly Subscription"}
                      </p>
                    </div>
                    {!item.isGift && (
                      <button onClick={() => removeFromCart(item.cartKey)} className="text-gray-400 hover:text-coral transition-colors">
                        <SafeIcon name="Trash2" className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-auto flex justify-between items-center">
                    {!item.isGift ? (
                      <div className="flex items-center border border-sand rounded-full px-2">
                        <button onClick={() => updateQuantity(item.cartKey, -1)} className="p-2 text-ube">-</button>
                        <span className="w-8 text-center text-sm font-bold">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.cartKey, 1)} className="p-2 text-ube">+</button>
                      </div>
                    ) : (
                      <span className="text-coral text-xs font-bold uppercase tracking-widest">Free Gift</span>
                    )}
                    <span className="font-bold text-gray-900">
                      {item.price === 0 ? 'FREE' : `$${(item.price * item.quantity).toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-sand sticky top-24">
              <h2 className="font-serif text-2xl mb-6">Order Summary</h2>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                </div>
                {isDiscountApplied && (
                  <div className="flex justify-between text-coral font-bold">
                    <span>15% Discount (UBEA15)</span>
                    <span>-${finalDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="pt-4 border-t border-sand flex justify-between text-xl font-bold text-gray-900">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <form onSubmit={handleApplyCoupon} className="mb-8">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Discount Code</label>
                <div className="flex gap-2">
                  <input type="text" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Enter code" className="flex-grow bg-sand-light border-transparent rounded-xl px-4 py-2 focus:ring-2 focus:ring-ube outline-none transition-all text-sm" />
                  <button type="submit" className="bg-ube text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-ube-light transition-colors">Apply</button>
                </div>
                {couponError && <p className="text-coral text-[10px] mt-2 font-bold">{couponError}</p>}
                {isDiscountApplied && <p className="text-ube text-[10px] mt-2 font-bold">Coupon applied! 15% off your first order.</p>}
              </form>
              <Button className="w-full py-4 text-lg">Checkout</Button>
              <div className="mt-6 flex items-center justify-center gap-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                <SafeIcon name="Lock" className="w-3 h-3" /> Secure Checkout
              </div>
            </div>

            <div className="mt-8 bg-lavender-light/30 rounded-2xl p-6 border border-lavender/20">
              {subtotal < 100 ? (
                <p className="text-xs text-ube-dark leading-relaxed">
                  Add <span className="font-bold">${(100 - subtotal).toFixed(2)}</span> more to receive a <strong>FREE Ube Essence (60 ml)</strong>!
                </p>
              ) : (
                <p className="text-xs text-ube-dark leading-relaxed">
                  🎉 <strong>Bonus Unlocked!</strong> We've added a Free Ube Essence to your bag.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;